import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Grid,
  Typography,
  Card,
  CardHeader,
  CardContent,
  TextField,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useApiGet, useApiSend } from '../../../utils/httpClient';
import {
  getSPDCommittees,
  patchSPDCommittees,
  postSPDCommittees,
  deleteSPDCommittees,
  replaceExistingCommittees,
  getCommitteePositions,
} from '../../../endpoints/chapters';
import { roles } from '../../../utils/variables';
import PositionDisplay from '../../../Components/PositionDisplay';
import AddPosition from '../../../Components/AddPosition';

const SPDCommittees = ({ id, spdAccess }) => {
  let [searchParams, setSearchParams] = useSearchParams();

  const updateSearchParams = useCallback(
    (key, value) => {
      if (!value || value === '') {
        searchParams.delete(key);
      } else {
        searchParams.set(key, value);
      }
      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams]
  );
  const [isSaving, setIsSaving] = useState(false);
  const [errorSaving, setErrorSaving] = useState(null);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [filteredCommittees, setFilteredCommittees] = useState([]);
  const {
    data: committeesData,
    isFetching,
    refetch,
  } = useApiGet(
    ['spd-committees', id],
    getSPDCommittees,
    {
      enabled: true,
      retry: 1,
    },
    {
      id,
    }
  );

  const handleSearch = (search) => {
    setSearch(search);
  };

  useEffect(() => {
    if (committeesData) {
      setFilteredCommittees(
        committeesData.filter((committee) =>
          committee.name.toLowerCase().includes(search.toLowerCase())
        )
      );
    }
  }, [committeesData, search]);

  useEffect(() => {
    updateSearchParams('search', search);
  }, [search, updateSearchParams]);

  const { data: committeePositionsData } = useApiGet(
    ['committee-positions'],
    getCommitteePositions,
    {
      enabled: true,
      retry: 1,
    }
  );

  const { mutate: updateCommittee } = useApiSend(
    patchSPDCommittees,
    (data) => {
      setIsSaving(false);
      refetch();
    },
    (error) => {
      setErrorSaving(
        error.message
          ? error.message
          : 'Error submitting request, if problem persists, please contact support'
      );
      setIsSaving(false);
    }
  );

  const { mutate: deleteCommittee } = useApiSend(
    deleteSPDCommittees,
    (data) => {
      setIsSaving(false);
      refetch();
    },
    (error) => {
      setErrorSaving(
        error.message
          ? error.message
          : 'Error submitting request, if problem persists, please contact support'
      );
      setIsSaving(false);
    }
  );
  const { mutate: postCommittee } = useApiSend(
    postSPDCommittees,
    (data) => {
      setIsSaving(false);
      refetch();
    },
    (error) => {
      setErrorSaving(
        error.message
          ? error.message
          : 'Error submitting request, if problem persists, please contact support'
      );
      setIsSaving(false);
    }
  );

  const { mutate: replaceCommittee } = useApiSend(
    replaceExistingCommittees,
    (data) => {
      setIsSaving(false);
      refetch();
    },
    (error) => {
      setErrorSaving(
        error.message
          ? error.message
          : 'Error submitting request, if problem persists, please contact support'
      );
      setIsSaving(false);
    }
  );

  const onSavePosition = (position) => {
    setIsSaving(true);
    setErrorSaving(null);
    if (position.action === 'newAppointment') {
      //Handle post
      if (!position.id) {
        //Handle new appointment
        postCommittee({
          formId: id,
          user_id: position.userId,
          start_date: position.startDate,
          end_date: null,
          position_id: position.positionId,
          committee_id: position.committeeId,
        });
      } else {
        replaceCommittee({
          id: position.id,
          user_id: position.userId,
          start_date: position.startDate,
          end_date: null,
        });
      }
    } else if (position.action === 'editDates') {
      //Handle patch
      updateCommittee({
        formId: id,
        id: position.id,
        start_date: position.startDate,
        end_date: position.endDate,
      });
    } else if (position.action === 'removeRecord') {
      //Handle delete
      deleteCommittee({
        formId: id,
        id: position.id,
      });
    }
  };

  if (isFetching) {
    return (
      <Grid item xs={12} textAlign={'center'} paddingTop={2}>
        <Typography variant="body1">Loading...</Typography>
      </Grid>
    );
  }
  if (!committeesData) {
    return (
      <Grid item xs={12} textAlign={'center'} paddingTop={2}>
        <Typography variant="body1">No data available</Typography>
      </Grid>
    );
  }

  const canEdit = () => {
    if (spdAccess) {
      let userRole = localStorage.getItem('role');
      let rolesAllowed = [roles.Admin, roles.Support, roles.SPDOfficer];
      return rolesAllowed.includes(userRole);
    }
    return false;
  };
  return (
    <Grid container>
      <Grid item xs={12} padding>
        <TextField
          label="Search"
          id="search-field"
          size="small"
          value={search}
          fullWidth={true}
          onChange={(event) => handleSearch(event.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Grid>
      {filteredCommittees.map((committee) => (
        <Grid item xs={12} md={6} padding key={committee.id}>
          <Card sx={{ p: 2, boxShadow: 3, borderRadius: 2 }}>
            <CardHeader
              title={committee.name}
              subheader={committee.description}
              sx={{ p: 1 }}
            />
            <CardContent sx={{ p: 1 }}>
              <Grid container spacing={2}>
                {committee.members
                  .sort((a, b) => a.order - b.order)
                  .map((user) => (
                    <PositionDisplay
                      id={user.id}
                      position={user.position}
                      positionId={user.position_id}
                      committeeId={user.committee_id}
                      name={user.name}
                      startDate={user.start_date}
                      endDate={user.end_date}
                      canEdit={canEdit()}
                      isSaving={isSaving}
                      errorSavingText={errorSaving}
                      onSave={onSavePosition}
                      userId={user.user_id}
                      pageFrom={'spd-committees'}
                      key={user.id}
                      spdChapterId={id}
                    />
                  ))}
                {canEdit() && (
                  <Grid item xs={12} padding>
                    <AddPosition
                      buttonText="Add Position"
                      committeeId={committee.id}
                      positions={committeePositionsData}
                      onAdd={onSavePosition}
                      isSaving={isSaving}
                      savingError={errorSaving}
                      spdChapterId={id}
                    />
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};

export default SPDCommittees;
